import {html, type PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {map} from "lit/directives/map.js";
import {repeat} from "lit/directives/repeat.js";
import {when} from "lit/directives/when.js";
import {DateTime} from "luxon";
import {container} from "tsyringe";
import {Entity} from "../../../domain/entities/entity";
import {EntityManager} from "../../../persistence/entity.manager";
import {ClassOf} from "../../../types/class-of.type";
import {AbstractInputComponent} from "../abstract-input.component";
import {EntityLiveRefreshController} from "../../_reactive-controllers/entity-live-refresh.controller";
import {RxjsLifecycleController} from "../../_reactive-controllers/rxjs-lifecycle.controller";
import {ComboboxItem} from "./combobox-item.interface";
import {StyleInfo, styleMap} from "lit/directives/style-map.js";
import {ClassInfo, classMap} from "lit/directives/class-map.js";
import {StringHelper} from "../../../helpers/string.helper";
import {toPopoverListItems} from "../combobox-utils/to-popover-list-items";
import {shouldCloseOnFocusOut} from "../combobox-utils/should-close-on-focus-out";
import {closePopoverOnEscape} from "../combobox-utils/close-popover-on-escape";
import {setupOutsideClickHandler, closeOtherPopovers} from "../combobox-utils/setup-outside-click-handler";
import {MultiSelectionController} from "@o2d/popover-list";
import type {SelectionChangedDetail} from "@o2d/popover-list";
import "@o2d/popover-list";
import {itemColorStyle} from "../../../helpers/header-item-style.helper";

@customElement(`ha-combobox-multi`)
export class HaComboboxMulti extends AbstractInputComponent {

    @property({type: Array})
    public _items: Array<ComboboxItem<string, Entity>> = [];

    @property({type: String})
    public placeholder = `Bitte wählen...`;

    @property({type: Boolean, attribute: `with-clear-button`})
    public withClearButton = false;

    @property({type: Boolean, attribute: `with-deleted`})
    public withDeleted = false;

    @property({type: Boolean, attribute: `with-filter`})
    public withFilter = false;

    @property({type: Boolean, attribute: `with-postfix`})
    public withPostfix = false;

    @property({type: Boolean, attribute: `with-prefix`})
    public withPrefix = false;

    @property({
        type: String,
        converter: value => {
            if (!StringHelper.isFilledString(value) || ![`default`, `visible`].includes(value as `default` | `visible`)) {
                return `default`;
            }

            return value as `default` | `visible`;
        }
    })
    public theme: `default` | `visible` = `default`;

    @state()
    private _selectedComboboxItems: ComboboxItem<string, Entity>[] | null = null;

    private _selectionController: MultiSelectionController | null = null;

    private _entity: ClassOf<Entity> | null = null;

    private _isReloading = false;
    private _reloadPending = false;
    public readonly rxjs = new RxjsLifecycleController(this);
    private readonly _liveRefreshController = new EntityLiveRefreshController(this, {
        getEntityClass: () => this.entity,
        onReload: () => this._handleLiveReload(),
    });

    @property({type: Object})
    public get entity(): ClassOf<Entity> | null {
        return this._entity;
    }

    public set entity(entity: ClassOf<Entity> | null) {
        if (!entity || entity === this._entity) {
            return;
        }

        const oldValue = this._entity;
        this._entity = entity;

        this._loadEntityItems(entity)
            .then(entities => {
                const itemsById = new Map(this._items.map(item => [item.id, item]));
                this._items = entities
                    .map(entity => (itemsById.has(entity.uid)) ? itemsById.get(entity.uid)! : this._mapEntityToComboboxItem(entity))
                    .sort((itemA, itemB) => itemA.title.localeCompare(itemB.title));
            });

        this.requestUpdate(`entity`, oldValue);
    }

    @state()
    private _value: Entity[] = [];

    @property({type: Array})
    public get value(): Entity[] {
        return (this._selectedComboboxItems ?? []).map(comboboxItem => comboboxItem.payload as Entity);
    }

    public set value(entities: (Entity & { deletedAt?: DateTime })[]) {
        if (!entities) {
            return;
        }

        const oldValue = this._value;
        this._value = entities;

        const selectedItems: ComboboxItem[] = [];
        const itemsTotal = this._items.length;
        const itemsById = new Map(this._items.map(item => [item.id, item]));

        for (const entity of entities) {
            let item = null;
            if (!itemsById.has(entity.uid)) {
                item = this._mapEntityToComboboxItem(entity);
                this._items.push(item);
            } else {
                item = itemsById.get(entity.uid)!;
            }

            selectedItems.push(item);
        }

        // wenn item dazugekommen ist → z.B. gelöschte Ressource
        if (itemsTotal !== this._items.length) {
            this._items = [...this._items].sort((itemA, itemB) => itemA.title.localeCompare(itemB.title));
        }

        this._selectedComboboxItems = selectedItems;
        this.requestUpdate(`value`, oldValue);
    }

    public render(): unknown {

        switch (this.theme) {
            case "visible":
                return this._renderThemeVisible();
            default:
                return html`
                    ${when(!!this.label, () => html`
                        <label @click=${this._onClickCombobox}>${this.label}</label>
                    `)}
                    <div class="wrapper"
                         @keydown=${this._onKeyDown}
                         @focusout=${this._onFocusOut}
                    >
                        <div class="wrapper-selected">
                            <div class="selected"
                                 @click=${this._onClickCombobox}
                                 tabindex="0"
                            >
                                ${this._renderLabels()}
                            </div>
                            ${when(this.withClearButton && this.value && (Array.isArray(this.value) && this.value.length > 0), () => html`
                                <div class="button clear-selection"
                                     @click=${this._clearSelection}
                                >
                                     <i class="icon icon-xmark clear-selection"></i>
                                </div>
                            `)}
                            <div class="button open-submenu"
                                 @click=${this._onClickCombobox}
                            >
                                <i class="icon icon-chevron-down"></i>
                            </div>
                        </div>
                        <o2d-popover-list
                            popover="manual"
                            ?filterable=${this.withFilter}
                            ?focus-filter=${this.withFilter}
                            .items=${toPopoverListItems(this._items.filter(item => !item.hidden))}
                        ></o2d-popover-list>
                    </div>
                    ${this._renderHelperText()}
                    ${this._renderErrors()}
                `;
        }
    }

    /** Sets up anchor positioning, instantiates MultiSelectionController, and binds selection-changed listener on host. */
    public firstUpdated(): void {
        const popover = this.querySelector('o2d-popover-list') as HTMLElement | null;
        const trigger = this.querySelector('.wrapper-selected') as HTMLElement | null;
        if (popover && trigger) {
            trigger.style.setProperty('anchor-name', `--${popover.id}`);
        }
        if (popover) {
            this._selectionController = new MultiSelectionController(this, popover);
            setupOutsideClickHandler(this, popover);
        }
        // Listen on host because MultiSelectionController dispatches selection-changed on the host,
        // not on o2d-popover-list — events bubble up, not down to children.
        this.addEventListener('selection-changed', (evt: Event) => this._onSelectionChanged(evt as CustomEvent<SelectionChangedDetail>));
    }

    /** Syncs selected ComboboxItems to MultiSelectionController when value changes. */
    public updated(changedProperties: PropertyValues): void {
        if (changedProperties.has('_value') && this._selectionController) {
            const selectedIds = (this._selectedComboboxItems ?? []).map(item => String(item.id));
            const popoverItems = toPopoverListItems(this._items.filter(item => !item.hidden));
            this._selectionController.setSelectedIds(selectedIds, popoverItems);
        }
    }

    /** Handles Escape key to close the popover, handing focus back to the trigger. */
    private _onKeyDown(evt: KeyboardEvent): void {
        closePopoverOnEscape(this, evt);
    }

    /**
     * Handles focusout to close the popover when focus leaves the component.
     * Delayed via requestAnimationFrame so that click events on non-focusable
     * popover items (li) fire before the popover is hidden.
     */
    private _onFocusOut(evt: FocusEvent): void {
        const popover = this.querySelector('o2d-popover-list') as HTMLElement | null;
        if (!popover?.matches(':popover-open')) return;

        const relatedTarget = evt.relatedTarget;
        requestAnimationFrame(() => {
            if (popover.matches(':popover-open') && shouldCloseOnFocusOut(this, popover, relatedTarget)) {
                popover.hidePopover();
            }
        });
    }

    private _clearSelection(): void {
        this.value = [];
        this._dispatchValueChangedEvent();
    }

    private _dispatchValueChangedEvent(): void {
        let {value} = this;

        this.dispatchEvent(
            new CustomEvent<{ value: Entity[] }>(
                `value-changed`,
                {detail: {value}}
            )
        )
    }

    private async _loadEntityItems(entityClass: ClassOf<Entity>): Promise<Entity[]> {
        return (container.resolve(EntityManager).getRepositoryForEntity(entityClass)?.findAll([], -1, 0, true)) ?? [];
    }

    /**
     * Reloads entity items in response to MessageBroker entity.* events and
     * resyncs `_selectedComboboxItems` via UID-match. Selected entries pointing
     * to entities that disappeared are removed and a `value-changed` event is
     * dispatched with the filtered array. Rename / color changes update the
     * matching ComboboxItem in-place via UID-match without dispatching.
     */
    private _handleLiveReload(): Promise<void> {
        return this._runReloadWithCoalescing(async () => {
            if (!this.entity) {
                return;
            }
            const freshEntities = await this._loadEntityItems(this.entity);
            if (!this.isConnected) {
                return;
            }

            this._rebuildItemsFromEntities(freshEntities);

            if (this._resyncSelectedItemsByUid()) {
                this._value = (this._selectedComboboxItems ?? []).map(item => item.payload as Entity);
                this._dispatchValueChangedEvent();
            }
        });
    }

    /**
     * Rebuilds `_items` from a fresh entity list (clears, re-maps, alphabetically sorts).
     */
    private _rebuildItemsFromEntities(entities: Entity[]): void {
        this._items = [];
        for (const entity of entities) {
            this._mapEntityToComboboxItem(entity);
        }
        this._items = [...this._items].sort((itemA, itemB) => itemA.title.localeCompare(itemB.title));
    }

    /**
     * Resyncs `_selectedComboboxItems` against current `_items` via UID-match.
     * Drops selections whose entity disappeared, keeps surviving ones with the fresh item
     * (so renamed labels propagate). Returns `true` when any selection was removed.
     */
    private _resyncSelectedItemsByUid(): boolean {
        const previousSelected = this._selectedComboboxItems ?? [];
        const itemsByUid = new Map(this._items.map(item => [item.id, item]));
        const newSelected: ComboboxItem<string, Entity>[] = [];
        for (const prev of previousSelected) {
            const fresh = itemsByUid.get(prev.id);
            if (fresh) {
                newSelected.push(fresh);
            }
        }
        this._selectedComboboxItems = newSelected;
        return newSelected.length !== previousSelected.length;
    }

    /**
     * Coalesces concurrent reload calls: at most one reload runs at a time,
     * and at most one additional reload is queued (collapsing bursts of events).
     */
    private async _runReloadWithCoalescing(fn: () => Promise<void>): Promise<void> {
        if (this._isReloading) {
            this._reloadPending = true;
            return;
        }
        this._isReloading = true;
        try {
            await fn();
        } finally {
            this._isReloading = false;
            if (this._reloadPending) {
                this._reloadPending = false;
                queueMicrotask(() => { void this._runReloadWithCoalescing(fn); });
            }
        }
    }

    private _mapEntityToComboboxItem(entity: Entity & { deletedAt?: DateTime; isDeleted?: boolean }): ComboboxItem {

        let item = this._items.find(i => i.id === entity.uid);
        if (!item) {
            const isDeleted = entity.isDeleted ?? !!entity.deletedAt;
            item = {
                id: entity.uid,
                title: entity.toString() + (isDeleted ? ' (gelöscht)' : ''),
                payload: entity,
                disabled: isDeleted,
                hidden: false,
                color: entity.color,
                textColor: entity.textColor,
                isDeleted
            }
            this._items.push(item);
        }

        return item;
    }

    private _onClickCombobox(evt: MouseEvent): void {
        if (this.readonly) {
            return;
        }

        switch (true) {
            case (evt.target as HTMLElement).classList.contains(`clear-selection`):
                this._clearSelection();
                break;
            case (evt.target as HTMLElement).classList.contains(`unselect-item`):
                break;
            default:
                this._togglePopover();
        }
    }

    private _onClickUnselectItem(item: ComboboxItem): void {
        if (item.disabled || this.readonly) {
            return;
        }

        this._selectedComboboxItems = (this._selectedComboboxItems ?? []).filter(selectedItem => item.id !== selectedItem.id);
        this._value = (this._selectedComboboxItems ?? []).map(item => item.payload as Entity);
        this._dispatchValueChangedEvent();
    }

    private _onClickSelectItem(item: ComboboxItem): void {
        if (item.disabled || this.readonly) {
            return;
        }
        this._selectedComboboxItems = [...new Set([...(this._selectedComboboxItems ?? []), item])];
        this._value = (this._selectedComboboxItems ?? []).map(item => item.payload as Entity);
        this._dispatchValueChangedEvent();
    }

    /**
     * Handles selection-changed event from MultiSelectionController.
     * Maps PopoverListItems back to ComboboxItems via ID lookup, then dispatches value-changed.
     */
    private _onSelectionChanged(evt: CustomEvent<SelectionChangedDetail>): void {
        const selectedPopoverItems = evt.detail.selectedItems;
        const itemsById = new Map(this._items.map(item => [item.id, item]));
        this._selectedComboboxItems = selectedPopoverItems
            .map(popoverItem => itemsById.get(popoverItem.id))
            .filter((item): item is ComboboxItem<string, Entity> => item !== undefined);
        this._dispatchValueChangedEvent();
    }

    private _renderLabels(): unknown {
        if (!!this.value && this.value.length > 0) {
            return repeat(
                this._selectedComboboxItems ?? [],
                item => item.id,
                item => {
                    const isDeleted = !!(item.payload as any)?.isDeleted || !!(item.payload as any)?.deletedAt;
                    // `--item-text-color` is set for both themes; only theme=visible has a rule that paints it
                    // (feature plan 0074) — the default-theme chips have no record background.
                    const itemStyles: Readonly<StyleInfo> = itemColorStyle(item.color ?? null, item.textColor ?? null);
                    const itemClasses: Readonly<ClassInfo> = {
                        label: true,
                        [`value-${StringHelper.toKebabCase(item.title)}`]: true,
                        'is-deleted': isDeleted
                    };
                    return html`
                        <span class=${classMap(itemClasses)}
                              data-sid=${item.id}
                              style=${styleMap(itemStyles)}
                              title=${isDeleted ? 'Dieser Eintrag wurde gelöscht' : ''}
                        >
                            <span class="title">${item.title}</span>
                            <i class="icon icon-xmark unselect-item"
                               @click=${() => this._onClickUnselectItem(item)}
                            ></i>
                        </span>
                    `
                }
            );
        }

        if (!!this.placeholder) {
            return html`<span class="placeholder">${this.placeholder}</span>`;
        }

        return ``;
    }

    /** Toggles popover visibility via native Popover API. Closes other open popovers first. */
    private _togglePopover(): void {
        const popover = this.querySelector('o2d-popover-list') as HTMLElement | null;
        if (!popover) return;

        if (popover.matches(':popover-open')) {
            popover.hidePopover();
        } else {
            closeOtherPopovers(popover);
            popover.showPopover();
        }
    }

    private _renderThemeVisible() {
        return html`
            ${when(!!this.label, () => html`
                <label @click=${this._onClickCombobox}>${this.label}</label>
            `)}
            <div class="wrapper">
                <div class="wrapper-selected">
                    <div class="selected">
                        ${this._renderLabels()}
                    </div>
                </div>    
                <div class="available-items">
                    ${repeat(this._items.filter(i => !i.hidden && !this._selectedComboboxItems?.includes(i)), item => item.id, item => {
    
                        const styles: Readonly<StyleInfo> = itemColorStyle(item.color ?? null, item.textColor ?? null);
                        const classes: Readonly<ClassInfo> = <ClassInfo>{
                            [`available-item`]: true,
                            [`value-${StringHelper.toKebabCase(item.title)}`]: true,
                            [`disabled`]: item.disabled
                        }

                        return html`
                            <div class=${classMap(classes)}
                                 data-sid=${item.id}
                                 style=${styleMap(styles)}
                                 @click=${() => this._onClickSelectItem(item)}>${item.title}
                            </div>
                        `
                    })}
                </div>
            </div>
            ${this._renderHelperText()}
        `
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ha-combobox-multi': HaComboboxMulti;
    }
}